import { type ICellEditorParams } from "ag-grid-community";
import { type FC, forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { ColorButton } from "widgets";

import { getCssColor } from "../../../../../app-utils/color.js";
import { type IAgGridCellProps } from "../../cell-base-interfaces.js";
import { TableCell } from "../core/TableCell.js";

const ColorCell: FC<IAgGridCellProps> = (props) => {
  const color = props.value;

  return (
    <TableCell>
      <ColorButton
        defaultColor={color}
        onClick={() => props.api.startEditingCell({ rowIndex: props.rowIndex, colKey: props.column })}
        size="tiny"
      />
    </TableCell>
  );
};

const ColorCellEditor = forwardRef<unknown, ICellEditorParams>(function ColorCellEditor(props: ICellEditorParams, ref) {
  const { value } = props;
  const [color, setColor] = useState<string>(getCssColor(value));

  const c = useCallback(() => {
    return {
      // the final tag to send to the grid, on completion of editing
      getValue(): string {
        return color;
      },

      // Gets called once before editing starts, to give editor a chance to
      // cancel the editing before it even starts.
      isCancelBeforeStart() {
        return false;
      },

      // Gets called once when editing is finished (eg if Enter is pressed).
      // If you return true, then the result of the edit will be ignored.
      isCancelAfterEnd() {
        // our editor will reject any tag greater than 1000
        return false;
      },

      isPopup() {
        return false;
      },
    };
  }, [color]);

  /* Component Editor Lifecycle methods */
  useImperativeHandle(ref, c);

  // this is a workaround to open the color picker immediately, because the isOpen of Dialog to true does misplace the dialog
  const [isColorPickerOpen, setColorPickerOpen] = useState<boolean>(false);
  useEffect(() => {
    setTimeout(() => {
      setColorPickerOpen(true);
    });
  }, []);

  return (
    <TableCell>
      <ColorButton
        defaultColor={color || "#ffffff"}
        isOpen={isColorPickerOpen}
        onApply={(newColor) => {
          setColor(newColor);
        }}
        onClose={() => {
          setColorPickerOpen(false);
          setTimeout(() => {
            props.api.stopEditing();
          });
        }}
        size="tiny"
      />
    </TableCell>
  );
});

export { ColorCell, ColorCellEditor };
